import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostRevaluation } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-revaluation-detail',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, FormsModule,
    TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule,
    JalaliDateInputComponent, CurrencySelectorComponent, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('catalog.costing.revaluations.detailDescription')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.revaluations.detailHelp.title'"
        [descriptionKey]="'catalog.costing.revaluations.detailHelp.description'"
        [bulletKeys]="['catalog.costing.revaluations.detailHelp.bullets.approve', 'catalog.costing.revaluations.detailHelp.bullets.post']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else if (isNew()) {
        <section class="panel">
          <a [routerLink]="['/inventory/costing/revaluations']" class="back-link">{{ t('catalog.action.backToList') }}</a>
          <div class="form-grid">
            <label><span>{{ t('catalog.inventory.reference') }}</span><input pInputText [(ngModel)]="createForm.referenceNumber" /></label>
            <label><span>{{ t('catalog.costing.revaluations.reason') }}</span><input pInputText [(ngModel)]="createForm.reason" /></label>
            <label><span>{{ t('columns.currency') }}</span><ain-currency-selector [(ngModel)]="createForm.currencyCode" /></label>
            <label><span>{{ t('catalog.costing.revaluations.effectiveAt') }}</span><ain-date-input [(ngModel)]="createForm.effectiveAtUtc" /></label>
          </div>
          <h4>{{ t('catalog.costing.revaluations.linesTitle') }}</h4>
          <div class="line-form">
            <p-select [(ngModel)]="lineDraft.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" appendTo="body" [placeholder]="t('catalog.inventory.offeringId')" />
            <p-select [(ngModel)]="lineDraft.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('catalog.inventory.warehouseId')" />
            <p-inputNumber [(ngModel)]="lineDraft.deltaValue" [placeholder]="t('catalog.costing.revaluations.deltaValue')" />
            <button pButton type="button" severity="secondary" [label]="t('catalog.costing.revaluations.addLine')" (click)="addLine()"></button>
          </div>
          <p-table [value]="createForm.lines">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.costing.revaluations.deltaValue') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row let-i="rowIndex">
              <tr>
                <td>{{ resolveOffering(row.offeringId) }}</td>
                <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                <td>{{ row.deltaValue }}</td>
                <td><button pButton type="button" size="small" severity="danger" [label]="t('common.delete')" (click)="removeLine(i)"></button></td>
              </tr>
            </ng-template>
          </p-table>
          <div class="actions" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'">
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="create()"></button>
          </div>
        </section>
      } @else if (!revaluation()) {
        <ain-state type="error" [title]="t('catalog.costing.revaluations.notFound')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <a [routerLink]="['/inventory/costing/revaluations']" class="back-link">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          <div class="summary">
            <div><strong>{{ t('catalog.inventory.reference') }}:</strong> {{ revaluation()!.referenceNumber }}</div>
            <div><strong>{{ t('columns.status') }}:</strong> {{ statusLabel(revaluation()!.status) }}</div>
            <div><strong>{{ t('catalog.costing.revaluations.reason') }}:</strong> {{ revaluation()!.reason }}</div>
            <div><strong>{{ t('columns.currency') }}:</strong> {{ revaluation()!.currencyCode }}</div>
          </div>
          <p-table [value]="revaluation()!.lines">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.costing.revaluations.deltaValue') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ resolveOffering(row.offeringId) }}</td>
                <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                <td>{{ row.deltaValue }}</td>
              </tr>
            </ng-template>
          </p-table>
          <div class="actions">
            @if (revaluation()!.status === 'Draft') {
              <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.costing.approve'" [label]="t('catalog.costing.revaluations.approve')" [disabled]="busy()" (click)="approve()"></button>
              <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.costing.revaluations.cancel')" [disabled]="busy()" (click)="cancel()"></button>
            }
            @if (revaluation()!.status === 'Approved') {
              <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.costing.approve'" [label]="t('catalog.costing.revaluations.post')" [disabled]="busy()" (click)="post()"></button>
            }
          </div>
        </section>
      }
      @if (error()) { <p class="error">{{ error() }}</p> }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin: 10px 0; }
    .form-grid label { display: grid; gap: 6px; }
    .line-form { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; align-items: center; }
    .summary { display: grid; gap: 6px; margin-bottom: 12px; }
    .actions { display: flex; gap: 8px; margin-top: 12px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryRevaluationDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly isNew = signal(false);
  readonly revaluation = signal<InventoryCostRevaluation | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  createForm = {
    referenceNumber: '',
    reason: '',
    currencyCode: 'USD',
    effectiveAtUtc: new Date().toISOString(),
    lines: [] as { offeringId: string; warehouseId: string; binId: string | null; lotId: string | null; deltaValue: number }[]
  };

  lineDraft = { offeringId: '', warehouseId: '', deltaValue: 0 };

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.isNew.set(id === 'new');
    if (this.isNew()) {
      this.loading.set(false);
      return;
    }
    this.load();
  }

  pageTitle(): string {
    return this.isNew()
      ? this.t('catalog.costing.revaluations.createTitle')
      : this.t('catalog.costing.revaluations.detailTitle');
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.loading.set(true);
    this.costingApi.getRevaluation(id).subscribe({
      next: row => {
        this.revaluation.set(row);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.revaluation.set(null);
        this.loading.set(false);
      }
    });
  }

  addLine(): void {
    if (!this.lineDraft.offeringId || !this.lineDraft.warehouseId) return;
    this.createForm.lines.push({
      offeringId: this.lineDraft.offeringId,
      warehouseId: this.lineDraft.warehouseId,
      binId: null,
      lotId: null,
      deltaValue: Number(this.lineDraft.deltaValue)
    });
    this.lineDraft = { offeringId: '', warehouseId: '', deltaValue: 0 };
  }

  removeLine(index: number): void {
    this.createForm.lines.splice(index, 1);
  }

  create(): void {
    if (!this.createForm.referenceNumber.trim() || !this.createForm.reason.trim() || this.createForm.lines.length === 0) {
      this.error.set(this.t('catalog.costing.revaluations.requiredFields'));
      return;
    }
    this.saving.set(true);
    this.costingApi.createRevaluation({
      referenceNumber: this.createForm.referenceNumber.trim(),
      reason: this.createForm.reason.trim(),
      currencyCode: this.createForm.currencyCode,
      effectiveAtUtc: this.createForm.effectiveAtUtc,
      lines: this.createForm.lines
    }).subscribe({
      next: saved => {
        this.saving.set(false);
        void this.router.navigate(['/inventory/costing/revaluations', saved.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.costing.states.saveFailed'));
      }
    });
  }

  approve(): void {
    const id = this.revaluation()?.id;
    if (!id) return;
    this.busy.set(true);
    this.costingApi.approveRevaluation(id).subscribe({
      next: row => { this.revaluation.set(row); this.busy.set(false); },
      error: () => { this.error.set(this.t('catalog.costing.revaluations.approveFailed')); this.busy.set(false); }
    });
  }

  post(): void {
    const id = this.revaluation()?.id;
    if (!id) return;
    this.busy.set(true);
    this.costingApi.postRevaluation(id, { idempotencyKey: crypto.randomUUID() }).subscribe({
      next: row => { this.revaluation.set(row); this.busy.set(false); },
      error: () => { this.error.set(this.t('catalog.costing.revaluations.postFailed')); this.busy.set(false); }
    });
  }

  cancel(): void {
    const id = this.revaluation()?.id;
    if (!id) return;
    this.busy.set(true);
    this.costingApi.cancelRevaluation(id).subscribe({
      next: row => { this.revaluation.set(row); this.busy.set(false); },
      error: () => { this.error.set(this.t('catalog.costing.revaluations.cancelFailed')); this.busy.set(false); }
    });
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));

  resolveOffering(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  statusLabel(status: string): string {
    const key = `catalog.costing.revaluations.status.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
